import { useEffect, useState } from 'react'
import { notification } from 'antd'
import { useGetPaymentMethodsOnFileQuery, usePayWithPaymentMethodOnFileMutation } from '../../../domain/invoicing/invoicingQueries'
import { InvoicePaymentStatus } from '../../../domain/invoicing/payment'
import { isStateValidForSubmit, markPaidManuallyErrorMessage, paymentMethodOnFileErrorMessage, PayInvoiceState, resolveAutoSelectedPayState } from './payInvoiceModel'

type PaymentStatusQuery = {
  data?: InvoicePaymentStatus
  isLoading: boolean
  isError: boolean
}

type MarkPaidMutation<TRequest> = {
  isLoading: boolean
  mutate: (request: TRequest, options: { onSuccess: () => void; onError: (error: any) => void }) => void
}

type Args<TRequest> = {
  isOpen: boolean
  onClose: () => void
  customerId: number
  documentLabel: string
  paymentStatusQuery: PaymentStatusQuery
  markPaidManuallyMutation: MarkPaidMutation<TRequest>
  buildMarkPaidRequest: (state: PayInvoiceState) => TRequest
}

export const usePayDocument = <TRequest>({
  isOpen,
  onClose,
  customerId,
  documentLabel,
  paymentStatusQuery,
  markPaidManuallyMutation,
  buildMarkPaidRequest
}: Args<TRequest>) => {
  const [payState, setPayState] = useState<PayInvoiceState>({
    option: 'markPaidManually',
    paymentMethodId: '',
    manualPaymentMethod: 'cash',
    checkNumber: ''
  })
  const paymentMethodsQuery = useGetPaymentMethodsOnFileQuery(customerId, { disabled: !isOpen })
  const payWithPaymentMethodOnFileMutation = usePayWithPaymentMethodOnFileMutation()

  const isLoading = paymentStatusQuery.isLoading || paymentMethodsQuery.isLoading
  const isError = paymentStatusQuery.isError || paymentMethodsQuery.isError
  const isSubmitting = payWithPaymentMethodOnFileMutation.isLoading || markPaidManuallyMutation.isLoading

  const paymentStatus = paymentStatusQuery.data
  const paymentMethods = paymentMethodsQuery.data ?? []

  useEffect(() => {
    if (isOpen && !isError && !isLoading) {
      setPayState(prev => ({ ...prev, ...resolveAutoSelectedPayState(paymentMethods, paymentStatus) }))
    }
  }, [isError, isLoading, paymentMethods, paymentStatus])

  useEffect(() => {
    if (isOpen) {
      setPayState(prev => ({ ...prev, manualPaymentMethod: 'cash', checkNumber: '' }))
    }
  }, [isOpen])

  const isValidForSubmit = isStateValidForSubmit(payState, paymentStatus)
  const submitDisabled = isLoading || isError || !isValidForSubmit || isSubmitting

  const handleSubmit = () => {
    if (!isValidForSubmit) {
      notification.warning({ message: 'There is a problem with the form, please check all fields then try again.' })
      return
    }

    if (payState.option === 'methodOnFile') {
      payWithPaymentMethodOnFileMutation.mutate({
        paymentId: paymentStatus!.paymentId!,
        paymentMethodId: payState.paymentMethodId
      }, {
        onSuccess: onClose,
        onError: (error: any) => {
          notification.error({ message: paymentMethodOnFileErrorMessage(error) })
        }
      })
      return
    }

    markPaidManuallyMutation.mutate(buildMarkPaidRequest(payState), {
      onSuccess: onClose,
      onError: (error: any) => {
        notification.error({ message: markPaidManuallyErrorMessage(error, documentLabel) })
      }
    })
  }

  return {
    payState,
    setPayState,
    isLoading,
    isError,
    isSubmitting,
    submitDisabled,
    paymentStatus,
    paymentMethods,
    handleSubmit
  }
}
